import { DOCUMENT } from '@angular/common';
import {
    ErrorHandler,
    Inject,
    Injectable,
    InjectionToken,
    isDevMode,
} from '@angular/core';
import * as Sentry from '@sentry/browser';
import { RewriteFrames } from '@sentry/integrations';

declare const __VERSION__: string;

export const SENTRY_TRACKED_DOMAINS = new InjectionToken<RegExp[]>('Sentry Tracked Domains');
export const SENTRY_DSN = new InjectionToken<string[]>('Sentry DSN');

@Injectable({
    providedIn: 'root',
})
export class SentryErrorHandler implements ErrorHandler {
    private _isSentryEnabled = false;
    private _handled = false;

    constructor(
        @Inject(DOCUMENT)
        _document: Document,
        @Inject(SENTRY_DSN)
        dsn: string,
        @Inject(SENTRY_TRACKED_DOMAINS)
        domains: RegExp[],
    ) {
        this._isSentryEnabled =
            domains.some(domain =>
                domain.test(
                    _document.location.hostname,
                ),
            ) ||
            isDevMode();
        if (!this._isSentryEnabled) { return; }

        const release = isDevMode() ?
            'DEV' :
            __VERSION__;

        const integrations = isDevMode() ?
            [] :
            [new RewriteFrames()];

        Sentry.init({
            dsn,
            release,
            beforeBreadcrumb: this._beforeBreadcrumb,
            integrations,
        });
    }

    public handleError(error: any) {
        if (!this._isSentryEnabled || this._handled) { return; }

        Sentry.captureException(error);
        this._handled = true;
    }

    private _beforeBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
        if (breadcrumb.category === 'xhr') {
            const parts = breadcrumb.data.url.split('?');
            const [url] = parts;
            const info = parts.length > 1 ? '[queryparams]' : '';

            breadcrumb.data.url = `${url}${info}`;
        }

        return breadcrumb;
    }
}
